import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    question: 'How does payment work?',
    answer: 'We show you a preview of the mockup first. If you like it, you pay and we build it out. For monthly services, billing is automatic on the 1st of each month. We accept bank transfer and all major cards.',
  },
  {
    question: 'What happens after my website launches?',
    answer: 'You get full ownership of the source code. If you are on a monthly plan, we handle hosting, backups, security updates, and content changes. If not, we hand everything over and you manage it yourself — though most clients stick with us for ongoing support.',
  },
  {
    question: 'Can I update the website myself?',
    answer: 'Absolutely. Every site we build includes a CMS (Content Management System) that lets you edit text, images, and blog posts without touching code. We also provide training if you need it.',
  },
  {
    question: 'What if I need more pages later?',
    answer: 'No problem. Existing clients get priority on new page builds. We quote per page based on complexity, or you can upgrade to a higher monthly plan that includes new page builds.',
  },
  {
    question: 'Do you work with businesses outside the UK?',
    answer: 'Yes. While we are based in London, we work with clients worldwide. All meetings are done via video call, and we accommodate different time zones.',
  },
  {
    question: 'What is your revision policy?',
    answer: 'Each build package includes a set number of revision rounds. Revisions cover design tweaks and content changes — not complete redesigns. We are transparent about scope so there are no surprises.',
  },
];

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section id="faq" className="py-24 bg-gray-50 dark:bg-secondary">
      <div className="max-w-3xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="text-highlight font-medium text-sm uppercase tracking-wider">FAQ</span>
          <h2 className="text-4xl md:text-5xl font-display font-bold text-primary dark:text-white mt-3 mb-4">
            Questions? Answered.
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Everything you need to know before getting started.
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-white dark:bg-dark rounded-xl overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left"
              >
                <span className="font-semibold text-primary dark:text-white">{faq.question}</span>
                <motion.span
                  animate={{ rotate: openIndex === index ? 180 : 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <ChevronDown size={20} className="text-gray-400" />
                </motion.span>
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <p className="px-6 pb-5 text-gray-600 dark:text-gray-400 leading-relaxed">
                      {faq.answer}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQ;
